import React, { useCallback, useEffect } from 'react';
import { getAuth, SAMLAuthProvider, signInWithPopup, User } from 'firebase/auth';

type UserDetails = {
    email: string;
    firstname: string;
    lastname: string;
    officeName: string;
}

export type AuthContextType = {
    user?: User;
    userDetails?: UserDetails;
    login: () => void;
    logout: () => void;
};

const AuthContext = React.createContext<AuthContextType>({
    login: () => {},
    logout: () => {},
});

export const AuthProvider = ({ children }: React.PropsWithChildren) => {
    const [user, setUser] = React.useState<User>();
    const [details, setDetails] = React.useState<UserDetails>();
    const [initialStateResolved, setInitialStateResolved] = React.useState(false);

    useEffect(() => {
        const auth = getAuth();
        auth.authStateReady().then(() => {
            console.log('auth state ready', auth.currentUser);
            setUser(auth.currentUser || undefined);
            setInitialStateResolved(true);
        });
    }, []);

    useEffect(() => {
        // @ts-expect-error token is there, but not in the types
        if (user?.accessToken) {
            console.log('user token changed', user);
            // @ts-expect-error token is there, but not in the types
            const decodedToken = parseJwt(user.accessToken!);

            if (decodedToken.firebase.sign_in_attributes) {
                setDetails({
                    email: decodedToken.firebase.sign_in_attributes.Email,
                    firstname: decodedToken.firebase.sign_in_attributes.FirstName,
                    lastname: decodedToken.firebase.sign_in_attributes.LastName,
                    officeName: decodedToken.firebase.sign_in_attributes.OfficeName
                });
            }
        }
        // @ts-expect-error token is there, but not in the types
    }, [user?.accessToken]);

    const login = useCallback(async () => {
        const provider = new SAMLAuthProvider('saml.unlockmls-saml');

        const result = await signInWithPopup(getAuth(), provider);

        console.log('login result', result);
        setUser(result.user);
    }, []);

    const logout = useCallback(async () => {
        await getAuth().signOut();
        setUser(undefined);
        setDetails(undefined);
    }, []);

    return (
        <AuthContext.Provider value={{ user, login, logout, userDetails: details }}>
            {initialStateResolved ? children : null}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return React.useContext(AuthContext);
};

function parseJwt(token: string) {
    var base64Url = token.split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
}